import type { GenerationSettings, GenerationSettingsField } from "../../src/generation-settings.ts";
import type { ProviderProfile, ProviderProfileId } from "../../src/provider-profiles.ts";
import {
  GIFME_MEDIA_BUSY_RETRY_AFTER_SECONDS,
  MAX_GIFME_BROWSER_MEDIA_ATTEMPT_MS,
  MAX_GIFME_BROWSER_MEDIA_BUSY_RETRIES,
  MAX_GIFME_BROWSER_MEDIA_REQUESTS,
  MAX_GIFME_BROWSER_MEDIA_RETRY_DELAY_MS,
} from "./constants.ts";

export interface PublicConfig {
  supabaseUrl: string;
  supabasePublishableKey: string;
}

export interface GenerationConfigView {
  id: string;
  name: string;
  revision: number;
  settings: GenerationSettings;
  createdByUserId: string | null;
  createdByEmail: string | null;
  changedByUserId: string | null;
  changedByEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GenerationConfigRegistryView {
  defaultConfigId: string;
  revision: number;
  changedByUserId: string | null;
  changedByEmail: string | null;
  updatedAt: string;
}

export interface GenerationConfigSurface {
  contractVersion: number;
  fields: GenerationSettingsField[];
  registry: GenerationConfigRegistryView;
  configs: GenerationConfigView[];
}

export interface ProviderRegistryView {
  contractVersion: 1;
  activeProfileId: ProviderProfileId;
  revision: number;
  changedByUserId: string | null;
  changedByEmail: string | null;
  updatedAt: string;
  profiles: readonly ProviderProfile[];
}

export interface RunMedia {
  selfie: string;
  source: string;
  sourcePreview: string;
  output: string;
  outputMp4?: string | null;
}

export type RunState = "QUEUED" | "GENERATING" | "FINALIZING" | "READY" | "FAILED";

export interface RunView {
  id: string;
  mode: "single";
  ownerUserId: string;
  ownerEmail: string;
  state: RunState;
  failureCode: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  generationSettingsContractVersion: 1;
  generationSettings: GenerationSettings;
  generationConfigId: string | null;
  generationConfigName: string | null;
  generationConfigRevision: number | null;
  providerProfileContractVersion: 1;
  providerProfile: ProviderProfile;
  selfieId: string | null;
  sourceKey: string | null;
  sourceGifId: string | null;
  source: string | null;
  sourcePreview: string | null;
  sourceOriginalName: string;
  sourceBytes: number | null;
  outputBytes: number | null;
  mp4Bytes?: number | null;
  media: RunMedia | null;
}

export interface RunPagination {
  offset: number;
  limit: number;
  total: number;
  page: number;
  pageCount: number;
}

export interface SelfieView {
  id: string;
  originalName: string;
  createdAt: string;
  mediaUrl: string;
}

export interface SourceAssetView {
  sourceGifId: string;
  sourceOriginalName: string;
  createdAt: string;
  mediaUrl: string;
}

export interface SourceView extends SourceAssetView {
  runId: string;
  ownerEmail: string;
}

export interface DiagnosticMediaView {
  path: string;
  sha256: string;
  bytes: number;
}

export interface RunDiagnosticsView {
  contractVersion: 1;
  submittedVariant: "original" | "previs" | "blurred";
  conformApplied: boolean;
  submittedInput: DiagnosticMediaView;
  providerPreConform: DiagnosticMediaView;
}

export interface ShareLinkView {
  id: string;
  runId: string;
  pageUrl: string;
  gifUrl: string;
}

export interface GifmeApiError extends Error {
  name: "GifmeApiError";
  status: number;
  code: string;
}

export type ShareHandoffResult =
  | { kind: "absent" }
  | { kind: "invalid" }
  | { kind: "ready"; source: SourceView };

export interface ApiClientOptions {
  getAccessToken(): string | null;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
  maxMediaRequests?: number;
  maxMediaBusyRetries?: number;
  mediaAttemptTimeoutMs?: number;
}

interface MediaWaiter {
  signal?: AbortSignal;
  resolve(): void;
  reject(error: DOMException): void;
  detach(): void;
}

type MediaAttempt =
  | { kind: "ready"; blob: Blob }
  | { kind: "busy"; retryAfterMs: number }
  | { kind: "failed"; status: number };

function apiError(status: number, code: string): GifmeApiError {
  return Object.assign(new Error(code), { name: "GifmeApiError" as const, status, code });
}

const USER_FACING_API_ERRORS = {
  authentication_required: "Your session expired. Sign in and try again.",
  invalid_token: "Your session expired. Sign in and try again.",
  domain_not_allowed: "Sign in with a @beyondlabs.net account.",
  provider_not_allowed: "Sign in with Google to cast.",
  cross_site_request: "Reload GIF Me before casting.",
  length_required: "The browser could not send the upload size. Choose the inputs again.",
  request_too_large: "The combined cast upload is too large.",
  idempotency_key_required: "This cast request could not be identified. Try submitting again.",
  multipart_form_required: "The cast inputs could not be read. Choose them again.",
  unknown_mode: "This cast format is no longer supported. Reload Studio.",
  choose_one_selfie: "Choose exactly one selfie.",
  choose_one_source: "Choose exactly one source GIF.",
  selfie_required: "Choose a selfie.",
  selfie_too_large: "That selfie is too large. Choose an image under 10 MB.",
  selfie_must_be_image: "Choose a PNG, JPEG, or WebP selfie.",
  selfie_not_available: "That saved selfie is no longer available. Choose another.",
  source_required: "Choose a source GIF.",
  source_too_large: "That source GIF is too large. Choose one under 100 MB.",
  source_must_be_gif: "Choose a valid GIF source file.",
  source_not_available: "That saved source GIF is no longer available. Choose another.",
  idempotency_key_conflict: "The cast inputs changed during a retry. Review them and submit again.",
  generation_settings_override_not_allowed: "Reload Studio before casting.",
  generation_config_name_conflict: "A configuration with this name already exists.",
  generation_config_revision_conflict:
    "This configuration changed elsewhere. Reload and review the latest version.",
  generation_config_registry_revision_conflict:
    "The default configuration changed elsewhere. Reload and review the latest state.",
  generation_config_not_found: "This configuration is no longer available.",
  invalid_generation_config_request: "The generation configuration is invalid.",
  invalid_generation_config_selection:
    "The selected generation configuration is invalid. Reload Studio.",
} satisfies Record<string, string>;

export function gifmeErrorMessage(reason: unknown, fallback: string): string {
  const message =
    reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";
  const known = USER_FACING_API_ERRORS[message as keyof typeof USER_FACING_API_ERRORS];
  if (known) return known;
  if (!message || /^[a-z][a-z0-9_]+$/.test(message)) return fallback;
  return message;
}

function mediaAbortError(): DOMException {
  return new DOMException("media request aborted", "AbortError");
}

function createMediaRequestLane(maxConcurrent: number) {
  const waiters: MediaWaiter[] = [];
  let active = 0;

  const acquire = (signal?: AbortSignal): Promise<void> => {
    if (signal?.aborted) return Promise.reject(mediaAbortError());
    if (active < maxConcurrent) {
      active += 1;
      return Promise.resolve();
    }
    return new Promise<void>((resolveSlot, rejectSlot) => {
      let isSettled = false;
      const remove = () => {
        const index = waiters.indexOf(waiter);
        if (index >= 0) waiters.splice(index, 1);
      };
      const onAbort = () => {
        if (isSettled) return;
        isSettled = true;
        remove();
        signal?.removeEventListener("abort", onAbort);
        rejectSlot(mediaAbortError());
      };
      const waiter: MediaWaiter = {
        signal,
        resolve: () => {
          if (isSettled) return;
          isSettled = true;
          signal?.removeEventListener("abort", onAbort);
          resolveSlot();
        },
        reject: rejectSlot,
        detach: () => signal?.removeEventListener("abort", onAbort),
      };
      signal?.addEventListener("abort", onAbort, { once: true });
      waiters.push(waiter);
    });
  };

  const release = (): void => {
    active -= 1;
    let next = waiters.shift();
    while (next) {
      next.detach();
      if (next.signal?.aborted) {
        next.reject(mediaAbortError());
        next = waiters.shift();
        continue;
      }
      active += 1;
      next.resolve();
      return;
    }
  };

  return async <T>(signal: AbortSignal | undefined, operation: () => Promise<T>): Promise<T> => {
    await acquire(signal);
    try {
      return await operation();
    } finally {
      release();
    }
  };
}

function mediaRetryDelayMs(response: Response): number {
  const value = response.headers.get("retry-after");
  const seconds = value && /^\d+$/.test(value) ? Number(value) : null;
  const delayMs = (seconds ?? GIFME_MEDIA_BUSY_RETRY_AFTER_SECONDS) * 1_000;
  return Math.min(delayMs, MAX_GIFME_BROWSER_MEDIA_RETRY_DELAY_MS);
}

async function waitForMediaRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) throw mediaAbortError();
  if (delayMs === 0) return;
  await new Promise<void>((resolveDelay, rejectDelay) => {
    let isSettled = false;
    const finish = (error?: DOMException) => {
      if (isSettled) return;
      isSettled = true;
      clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      if (error) rejectDelay(error);
      else resolveDelay();
    };
    const onAbort = () => finish(mediaAbortError());
    const timeout = setTimeout(() => finish(), delayMs);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function withMediaAttemptTimeout<T>(
  timeoutMs: number,
  callerSignal: AbortSignal | undefined,
  operation: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  if (callerSignal?.aborted) throw mediaAbortError();
  const controller = new AbortController();
  let didTimeout = false;
  let rejectAbort = (_error: DOMException) => {};
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAbort = reject;
  });
  const onCallerAbort = () => {
    controller.abort();
    rejectAbort(mediaAbortError());
  };
  callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
  const timeout = setTimeout(() => {
    didTimeout = true;
    controller.abort();
    rejectAbort(mediaAbortError());
  }, timeoutMs);
  try {
    return await Promise.race([operation(controller.signal), aborted]);
  } catch (error) {
    if (callerSignal?.aborted) throw mediaAbortError();
    if (didTimeout) throw apiError(408, "media_timeout");
    throw error;
  } finally {
    clearTimeout(timeout);
    callerSignal?.removeEventListener("abort", onCallerAbort);
  }
}

export function createApiClient(options: ApiClientOptions) {
  const fetchImpl =
    options.fetchImpl ?? (async (input: string, init?: RequestInit) => fetch(input, init));
  const withMediaRequestSlot = createMediaRequestLane(
    options.maxMediaRequests ?? MAX_GIFME_BROWSER_MEDIA_REQUESTS,
  );
  const maxMediaBusyRetries = options.maxMediaBusyRetries ?? MAX_GIFME_BROWSER_MEDIA_BUSY_RETRIES;
  const mediaAttemptTimeoutMs = options.mediaAttemptTimeoutMs ?? MAX_GIFME_BROWSER_MEDIA_ATTEMPT_MS;
  if (!Number.isSafeInteger(mediaAttemptTimeoutMs) || mediaAttemptTimeoutMs <= 0) {
    throw new Error("media attempt timeout must be a positive safe integer");
  }

  async function request(path: string, init: RequestInit = {}): Promise<Response> {
    const token = options.getAccessToken();
    if (!token) throw apiError(401, "authentication_required");
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${token}`);
    return await fetchImpl(path, { ...init, headers });
  }

  async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await request(path, init);
    const payload = (await response.json().catch(() => null)) as { error?: unknown } | null;
    if (!response.ok) {
      const code = typeof payload?.error === "string" ? payload.error : `http_${response.status}`;
      throw apiError(response.status, code);
    }
    return payload as T;
  }

  return {
    request,
    json,
    async saveSelfie(file: File): Promise<SelfieView> {
      const body = new FormData();
      body.set("selfie", file);
      const payload = await json<{ selfie: SelfieView }>("/api/selfies", {
        method: "POST",
        body,
      });
      return payload.selfie;
    },
    async mediaBlob(path: string, signal?: AbortSignal): Promise<Blob> {
      let busyRetries = 0;
      while (true) {
        const attempt = await withMediaRequestSlot(
          signal,
          async (): Promise<MediaAttempt> =>
            await withMediaAttemptTimeout(
              mediaAttemptTimeoutMs,
              signal,
              async (attemptSignal): Promise<MediaAttempt> => {
                const response = await request(path, { signal: attemptSignal });
                let isBodyConsumed = false;
                const cancelBody = () => {
                  if (!isBodyConsumed) void response.body?.cancel().catch(() => {});
                };
                attemptSignal.addEventListener("abort", cancelBody, { once: true });
                try {
                  if (attemptSignal.aborted) throw mediaAbortError();
                  if (response.ok) {
                    const blob = await response.blob();
                    isBodyConsumed = true;
                    return { kind: "ready", blob };
                  }
                  if (response.status === 503) {
                    const retryAfterMs = mediaRetryDelayMs(response);
                    const payload = (await response.json().catch(() => null)) as {
                      error?: unknown;
                    } | null;
                    isBodyConsumed = true;
                    if (payload?.error === "media_busy") return { kind: "busy", retryAfterMs };
                  } else {
                    await response.body?.cancel().catch(() => {});
                    isBodyConsumed = true;
                  }
                  return { kind: "failed", status: response.status };
                } finally {
                  attemptSignal.removeEventListener("abort", cancelBody);
                  cancelBody();
                }
              },
            ),
        );
        if (attempt.kind === "ready") return attempt.blob;
        if (attempt.kind === "failed") {
          throw apiError(attempt.status, `media_http_${attempt.status}`);
        }
        if (busyRetries >= maxMediaBusyRetries) throw apiError(503, "media_busy");
        busyRetries += 1;
        await waitForMediaRetry(attempt.retryAfterMs, signal);
      }
    },
    async getRunDiagnostics(
      runId: string,
      signal?: AbortSignal,
    ): Promise<RunDiagnosticsView | null> {
      const payload = await json<{ diagnostics: RunDiagnosticsView | null }>(
        `/api/runs/${runId}/diagnostics`,
        { signal },
      );
      return payload.diagnostics;
    },
    async listShareLinks(): Promise<ShareLinkView[]> {
      const payload = await json<{ links: ShareLinkView[] }>("/api/share-links");
      return payload.links;
    },
    async createShareLink(runId: string): Promise<ShareLinkView> {
      const payload = await json<{ link: ShareLinkView }>(`/api/runs/${runId}/share-links`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      return payload.link;
    },
    async revokeShareLink(id: string): Promise<void> {
      const response = await request(`/api/share-links/${id}`, { method: "DELETE" });
      if (!response.ok) throw apiError(response.status, `http_${response.status}`);
    },
    async consumeShareHandoff(): Promise<ShareHandoffResult> {
      const response = await request("/api/share-handoff", { method: "POST" });
      if (response.status === 204) return { kind: "absent" };
      const payload = (await response.json().catch(() => null)) as {
        source?: SourceView;
        error?: unknown;
      } | null;
      if (response.status === 410 && payload?.error === "handoff_invalid") {
        return { kind: "invalid" };
      }
      if (!response.ok || !payload?.source) {
        const code = typeof payload?.error === "string" ? payload.error : `http_${response.status}`;
        throw apiError(response.status, code);
      }
      return { kind: "ready", source: payload.source };
    },
  };
}

export type GifmeApiClient = ReturnType<typeof createApiClient>;
